import { useState, useEffect, useCallback } from "react";
import { sqlRead, payloadOf } from "../../utils/sqlClient";
import { ARCHITECTURAL_QUESTIONS } from "../../config/architecturalQuestions";
import MultiSelectWithOther from "../../components/MultiSelectWithOther";

// Detail Module 1 (Architectural) of the Scope Delivery Method gate (db/83):
// shown on the Revisions card when a revision's ScopeDeliveryMethod = 'Box'.
// Per-room questionnaire (db/98_architectural_room_details.sql).

function emptyAnswers() {
  const a = {};
  for (const q of ARCHITECTURAL_QUESTIONS) a[q.key] = [];
  return a;
}

function summarize(answers) {
  if (!answers) return "No answers yet";
  const count = ARCHITECTURAL_QUESTIONS
    .map(q => (answers[q.key] || []).length)
    .reduce((sum, n) => sum + n, 0);
  return count === 0 ? "No answers yet" : `${count} answer${count === 1 ? "" : "s"} selected`;
}

export default function ArchitecturalRoomsPanel({ revisionId, isLocked }) {
  const [rooms, setRooms]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRoom, setEditingRoom] = useState(null); // room object being added/edited, or null

  const load = useCallback(() => {
    if (!revisionId) return;
    setLoading(true);
    sqlRead(`EXEC sp_GetScopeArchRooms @revision_id=${revisionId}`)
      .then(rows => setRooms(rows || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [revisionId]);

  useEffect(() => { load(); }, [load]);

  const openAddRoom = () => {
    setEditingRoom({
      RoomID: null,
      RoomName: "",
      DisplayOrder: rooms.length,
      answers: emptyAnswers(),
    });
  };

  const openEditRoom = (room) => {
    let answers = emptyAnswers();
    try {
      if (room.ArchAnswersJson) answers = { ...answers, ...JSON.parse(room.ArchAnswersJson) };
    } catch { /* malformed JSON — start fresh */ }
    setEditingRoom({ RoomID: room.RoomID, RoomName: room.RoomName, DisplayOrder: room.DisplayOrder, answers });
  };

  const saveRoom = async () => {
    if (!editingRoom.RoomName.trim()) { alert("Room name is required."); return; }
    try {
      await sqlRead(`EXEC sp_SaveScopeArchRoom @payload = '${payloadOf({
        revision_id: revisionId,
        room_id: editingRoom.RoomID,
        room_name: editingRoom.RoomName,
        display_order: editingRoom.DisplayOrder,
        arch_answers: editingRoom.answers,
      })}'`);
      setEditingRoom(null);
      load();
    } catch (e) {
      console.error(e);
      alert("Error saving room: " + e.message);
    }
  };

  const deleteRoom = async (roomId) => {
    if (!confirm("Delete this room?")) return;
    try {
      await sqlRead(`EXEC sp_DeleteScopeArchRoom @payload = '${payloadOf({ room_id: roomId })}'`);
      load();
    } catch (e) {
      console.error(e);
      alert("Error deleting room: " + e.message);
    }
  };

  const cardStyle = { border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", background: "var(--surface)", padding: "10px 12px" };

  if (loading) return <div style={{ fontSize: 12, color: "var(--text3)" }}>Loading rooms…</div>;

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--cs-border)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>🏗️ Architectural — Rooms</div>
        {!isLocked && (
          <button
            onClick={openAddRoom}
            style={{ padding: "5px 10px", fontSize: 11, fontWeight: 500, background: "var(--cs-navy)", color: "#fff", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}
          >
            + Add Room
          </button>
        )}
      </div>

      {rooms.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: "center", color: "var(--text3)", fontSize: 12, padding: "16px" }}>
          No rooms yet. {!isLocked && "Click \"+ Add Room\" to start the architectural questionnaire."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rooms.map(room => (
            <div key={room.RoomID} style={{ ...cardStyle, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button
                onClick={() => openEditRoom(room)}
                style={{ background: "none", border: "none", textAlign: "left", cursor: "pointer", flex: 1 }}
              >
                <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text)" }}>{room.RoomName}</div>
                <div style={{ fontSize: 10, color: "var(--text3)" }}>{summarize(JSON.parse(room.ArchAnswersJson || "{}"))}</div>
              </button>
              {!isLocked && (
                <button
                  onClick={() => deleteRoom(room.RoomID)}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--red)", padding: "4px 8px" }}
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editingRoom && (
        <RoomEditorModal
          room={editingRoom}
          onChange={setEditingRoom}
          onSave={saveRoom}
          onCancel={() => setEditingRoom(null)}
        />
      )}
    </div>
  );
}

function RoomEditorModal({ room, onChange, onSave, onCancel }) {
  const inputStyle = { height: 32, border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", padding: "0 10px", fontSize: 13, color: "var(--text)", width: "100%", outline: "none", boxSizing: "border-box" };

  const setAnswer = (key, values) => {
    onChange({ ...room, answers: { ...room.answers, [key]: values } });
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "var(--surface)", borderRadius: "var(--radius-lg)", width: "100%", maxWidth: 560, maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cs-border)", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>{room.RoomID ? "Edit Room" : "Add Room"}</div>
          <button onClick={onCancel} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "var(--text3)" }}>✕</button>
        </div>

        <div style={{ padding: 18, overflowY: "auto", display: "flex", flexDirection: "column", gap: 18 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 4 }}>Room Name</label>
            <input
              type="text"
              value={room.RoomName}
              onChange={e => onChange({ ...room, RoomName: e.target.value })}
              placeholder="e.g. Lab 101"
              autoFocus
              style={inputStyle}
            />
          </div>

          {ARCHITECTURAL_QUESTIONS.map(q => (
            <div key={q.key}>
              <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text)", display: "block", marginBottom: 6 }}>{q.label}</label>
              <MultiSelectWithOther
                options={q.options}
                allowOther={q.allowOther}
                value={room.answers[q.key] || []}
                onChange={values => setAnswer(q.key, values)}
              />
            </div>
          ))}
        </div>

        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--cs-border)", display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
          <button onClick={onCancel} style={{ padding: "8px 16px", fontSize: 13, background: "none", border: "1px solid var(--cs-border)", borderRadius: "var(--radius)", cursor: "pointer", color: "var(--text2)" }}>Cancel</button>
          <button onClick={onSave} style={{ padding: "8px 20px", fontSize: 13, fontWeight: 500, background: "var(--cs-navy)", color: "#fff", border: "none", borderRadius: "var(--radius)", cursor: "pointer" }}>
            Save Room
          </button>
        </div>
      </div>
    </div>
  );
}
